'use client'

import PlaceBidLabel from './place-bid-label'
import Loading from '@/components/circle-loading'
import BitcoinIcon from '@/icons/bitcoin-filled'
import InstantSell from './instant-sell'

import { useEffect, useState, type FC } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { useCollectionOffers } from '@/utils/hooks/useOffers'
import { offersEnabledFor } from '@/utils/offers'
import { activeCollectionOffers } from '@/utils/offer-book'
import useIsHydrated from '@/utils/hooks/useIsHydrated'
import useLoggedIn from '@/utils/hooks/useLoggedIn'
import Wallet from '@/utils/wallet'
import MakeOfferModal from './make-offer-modal'
import OfferDialog from './offer-dialog'
import CollectionOfferBook, { bookBtc, type OfferSelection } from './collection-offer-book'
import CollectionIcon from '@/components/collection-icon'
import useBidReference from '@/utils/hooks/useBidReference'
import type { Collection } from '@/utils/types/collections'
import styles from './collection-offer-book.module.css'
import tradeStyles from './trade-panel.module.css'

const CollectionOffersPanel: FC<{
	slug: string
	name?: string | null
	floorSats?: number | null
	collection?: Collection
	compact?: boolean
	actionsOnly?: boolean
	hideInstantSell?: boolean
	stackActions?: boolean
	showBidRebate?: boolean
	defaultCollapsed?: boolean
	hideUnavailableSell?: boolean
}> = ({ slug, name, floorSats, collection, actionsOnly = false, hideInstantSell = false, stackActions = false, showBidRebate = false, defaultCollapsed = false, hideUnavailableSell = false }) => {
	const hydrated = useIsHydrated()
	const enabled = hydrated && offersEnabledFor(slug)
	const { data, isLoading, isValidating, error, mutate } = useCollectionOffers(
		enabled ? slug : null
	)
	const loggedIn = useLoggedIn()
	const wallet = loggedIn ? Wallet.getTaprootAddress() : null
	const bidRef = useBidReference(enabled ? slug : null, floorSats)
	const [open, setOpen] = useState(!defaultCollapsed)
	const [bookOpen, setBookOpen] = useState(false)
	const [selection, setSelection] = useState<OfferSelection | null>(null)
	const [now, setNow] = useState(Date.now)
	useEffect(() => {
		if (!enabled) return
		const timer = setInterval(() => setNow(Date.now()), 1000)
		return () => clearInterval(timer)
	}, [enabled])
	if (!enabled) return null
	const offers = activeCollectionOffers(data, now)
	const collectionOffers = offers.filter((o) => o.scope === 'collection')
	const top = collectionOffers[0]?.price_sats
	const onOffer = (next: OfferSelection) => {
		setBookOpen(false)
		setSelection(next)
	}
	const status =
		isLoading && !data ? (
			<div role="status" aria-label="Loading offers" className="flex justify-center py-8"><Loading className="!w-6 !h-6" /></div>
		) : error ? (
			<p className={styles.status} role="status">
				{isValidating
					? 'Reconnecting to offers…'
					: data
					? 'Showing last loaded offers. Refresh unavailable.'
					: 'Offers are temporarily unavailable.'}
				<button type="button" disabled={isValidating} onClick={() => void mutate()}>
					Retry
				</button>
			</p>
		) : null
	return (
		<section
			className={`${styles.panel} ${actionsOnly ? `${styles.actionsOnly} w-full max-w-[100vw]` : ''}`}
			aria-label="Collection offers"
		>
			{!actionsOnly && (
				<header className={styles.heading}>
					<button type="button" aria-expanded={open} onClick={() => setOpen(!open)}>
						Offers {offers.length > 0 && <small>{offers.length} active</small>}
						{open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
					</button>
				</header>
			)}
			{open && (
				<div className={styles.content}>
					<div className={`${tradeStyles.actions} ${styles.offerActions}`} style={hideInstantSell || stackActions ? { gridTemplateColumns: 'minmax(0, 1fr)' } : undefined}>
						{!hideInstantSell && <InstantSell slug={slug} name={name} compact showAmount={stackActions && showBidRebate} hideUnavailable={hideUnavailableSell} />}
						<button
							type="button"
							className={tradeStyles.primary} style={stackActions || showBidRebate ? { order: -1 } : undefined}
							onClick={(e) => {
								e.currentTarget.focus({ preventScroll: true })
								onOffer({ scope: 'collection', price: bidRef.sats ?? floorSats ?? 0 })
							}}
						>
							<PlaceBidLabel rebate={showBidRebate} />
						</button>
					</div>
					{status}
					{data && !actionsOnly && (
						<>
							<div className={styles.stats}>
								<div>
									<span>Instant Sell</span>
									<strong>
										{top ? (
											<>
												<BitcoinIcon />
												{bookBtc(top)}

											</>
										) : (
											'—'
										)}
									</strong>
								</div>
								<div>
									<span>Floor price</span>
									<strong>
										{bidRef.fairSats ? (
											<>
												<BitcoinIcon />
												{bookBtc(bidRef.fairSats)}

											</>
										) : (
											'—'
										)}
									</strong>
								</div>
								<div>
									<span>Lowest listing</span>
									<strong>
										{floorSats ? (
											<>
												<BitcoinIcon />
												{bookBtc(floorSats)}

											</>
										) : (
											'—'
										)}
									</strong>
								</div>
							</div>
							<button type="button" className={styles.expand} onClick={() => setBookOpen(true)}>
								View order book
							</button>
						</>
					)}
				</div>
			)}
			{bookOpen && (
				<OfferDialog
					headerDescription
					narrow
					title={`${name || slug} offers`}
					description="Compare funded offers, see buyer demand, and choose your price."
					onClose={() => setBookOpen(false)}
					footer={
						<button
							type="button"
							className={styles.primary}
							onClick={() => onOffer({ scope: 'collection', price: bidRef.sats ?? floorSats ?? 0 })}
						>
							Make collection offer
						</button>
					}
				>
					{status}
					<CollectionOfferBook offers={offers} wallet={wallet} now={now} onOffer={onOffer} full />
				</OfferDialog>
			)}
			{selection && (
				<MakeOfferModal
					open
					onClose={() => setSelection(null)}
					collectionSlug={slug}
					initialScope={selection.scope}
					initialPriceSats={selection.price}
					initialTrait={selection.trait}
					traitOffers={offers.filter((o) => o.scope === 'trait')}
					supply={Number(collection?.total_supply) || undefined}
					collectionIcon={collection ? <CollectionIcon collection={collection} /> : undefined}
					name={name || slug}
					reference={{
						floorSats: floorSats ?? null,
						fairSats: bidRef.fairSats,
						topOfferSats: top || null
					}}
					onPlaced={() => void mutate()}
				/>
			)}
		</section>
	)
}
export default CollectionOffersPanel
